import { FC, useEffect, useState } from 'react';
import Script from 'next/script';

import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';
import { timeToMilliseconds } from 'utils/time';
import { parseStringToBoolean } from 'utils/boolean';

const CONTAINER_ID = process.env.NEXT_PUBLIC_CRIMTAN_CONTAINER_ID ?? '';
const SCRIPT_ID = process.env.NEXT_PUBLIC_CRIMTAN_SCRIPT_ID ?? '';
const ENABLE_COOKIE_CONSENT = parseStringToBoolean(
  process.env.NEXT_PUBLIC_COOKIE_CONSENT
);

const COOKIE_REGEX = /^(.*;)?\s*OptanonConsent\s*=\s*[^;]+(.*)?$/;

const getHasExistingCookie = (): boolean | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const hasConsents = document.cookie.match(COOKIE_REGEX) !== null;
  return hasConsents;
};

export const CrimtanConsentScripts: FC = () => {
  const [shouldLoadScripts, setShouldLoadScripts] = useState(false);
  const [numLoadedScripts, setNumLoadedScripts] = useState(0);

  const handleScriptLoaded = () => {
    setNumLoadedScripts((prev) => prev + 1);
  };

  useIsomorphicLayoutEffect(() => {
    if (shouldLoadScripts) {
      return;
    }
    const hasExistingCookie = getHasExistingCookie();
    if (!hasExistingCookie) {
      setShouldLoadScripts(true);
    } else {
      setTimeout(() => {
        setShouldLoadScripts(true);
      }, timeToMilliseconds(3.5, 'seconds'));
    }
  }, [shouldLoadScripts]);

  useEffect(() => {
    if (numLoadedScripts === 3) {
      try {
        window.__tcfapi('getTCData', 2, (tcData, success) => {
          if (success) {
            window.consenTag.init(
              { containerId: CONTAINER_ID, silentMode: true },
              tcData.tcString as string
            );
          } else {
            window.consenTag.init(
              { containerId: CONTAINER_ID, silentMode: true },
              false
            );
          }
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, [numLoadedScripts]);

  if (!ENABLE_COOKIE_CONSENT || !shouldLoadScripts) {
    return null;
  }

  return (
    <>
      <Script
        strategy="lazyOnload"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        type="text/javascript"
        data-domain-script={SCRIPT_ID}
        defer
        onLoad={handleScriptLoaded}
      />
      <Script
        strategy="lazyOnload"
        src="https://cdn.cookielaw.org/consent/tcf.stub.js"
        defer
        type="text/javascript"
        onLoad={handleScriptLoaded}
      />
      <Script
        strategy="lazyOnload"
        src="https://consentag.eu/public/3.0.1/consenTag.js"
        defer
        onLoad={handleScriptLoaded}
      />
    </>
  );
};

declare global {
  interface Window {
    /**
     * Manually typed from documentation:
     * https://documentation.sourcepoint.com/api/gdpr-tcf-v2-api/iab-__tcfapi-function
     */
    __tcfapi(
      command:
        | 'getTCData'
        | 'ping'
        | 'addEventListener'
        | 'removeEventListener',
      version: number,
      callback: (tcData: Record<string, unknown>, success: boolean) => void,
      vendorIds?: number[]
    ): void;

    /**
     * Inferred types from how consentag is used, but no documentation available.
     */
    consenTag: {
      init: (
        options: { containerId: string; silentMode: boolean },
        data: string | boolean
      ) => void;
    };
  }
}
